import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import type { NotificationFindInterface } from '@zaoblako/shared/modules/notification/dtos/notification-find.dto';
import { NotificationKindEnum } from '@zaoblako/shared/modules/notification/enums/notification.kind.enum';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { NotificationService } from '@zaoblako/web/core/services/notification/notification.service';
import { WebsocketNotificationService } from '@zaoblako/web/core/services/notification/websocket-notification.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { GlobalSearchComponent } from '@zaoblako/web/shared/components/global-search/global-search.component';
import { NotificationExpiredListComponent } from '@zaoblako/web/shared/components/notification/expired-list/notification-expired-list.component';
import { NotificationFavoritesListComponent } from '@zaoblako/web/shared/components/notification/favorites-list/notification-favorites-list.component';
import { NotificationListComponent } from '@zaoblako/web/shared/components/notification/list/notification-list.component';
import { NotificationTaskListComponent } from '@zaoblako/web/shared/components/notification/task-list/notification-task-list.component';
import { SupportModalComponent } from '@zaoblako/web/shared/components/support-modal/support-modal.component';
import { VoiceAssistantModalComponent } from '@zaoblako/web/shared/voice-assistant/components/modal/voice-assistant-modal.component';
import { ConvertNotificationToLinkPipe } from '@zaoblako/web/shared/pipes/convert-notification-to-link.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { NotificationActionEnum } from '@zaoblako/shared/modules/notification/enums/notification.action.enum';

import { Toast, ToastModule } from 'primeng/toast';
import { isNil } from 'lodash';
import { filter } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss'],
  imports: [
    CommonModule,
    GlobalSearchComponent,
    NotificationListComponent,
    NotificationFavoritesListComponent,
    NotificationExpiredListComponent,
    ToastModule,
    TruncatePipe,
    SafeHtmlPipe,
    NotificationTaskListComponent,
    SupportModalComponent,
    VoiceAssistantModalComponent
  ],
  providers: [ConvertNotificationToLinkPipe],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationPanelComponent implements OnInit {
  @ViewChild(GlobalSearchComponent) private globalSearch?: GlobalSearchComponent;

  public listShowNotification: NotificationKindEnum[] = [];
  public access: { canReadBrsTask: boolean; canReadProject: boolean; canReadDeal: boolean; canReadMailing: boolean };

  public readonly notificationKindInfo: Record<string, NotificationKindEnum[]> = {
    BASIS: [NotificationKindEnum.PERSONAL],
    FINANCE: [NotificationKindEnum.TASK, NotificationKindEnum.DEAL],
    HELPDESK: [NotificationKindEnum.HELPDESK],
    PROJECT: [NotificationKindEnum.PROJECT],
    DEBTS: [NotificationKindEnum.DEBT],
    PROCUREMENT: [NotificationKindEnum.PURCHASE]
  };

  public get NotificationKind() {
    return NotificationKindEnum;
  }

  public get isShowExpiredNotification() {
    return [NotificationKindEnum.TASK, NotificationKindEnum.PROJECT, NotificationKindEnum.HELPDESK].some((kind) =>
      this.listShowNotification.includes(kind)
    );
  }

  public constructor(
    private readonly authService: AuthService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly websocketNotificationService: WebsocketNotificationService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly convertNotificationToLinkPipe: ConvertNotificationToLinkPipe,
    private readonly accessService: AccessService
  ) {
    this.access = {
      canReadBrsTask: this.accessService.brs.brsTaskRights.canReadBrsTask,
      canReadProject: this.accessService.project.projectRights.canReadProject,
      canReadDeal: this.accessService.finance.dealRights.canReadDeal,
      canReadMailing: this.accessService.debts.debtsRights.canReadMailing
    };
  }

  public ngOnInit(): void {
    this.websocketNotificationService.createWSConnection(this.authService.getToken, this.authService?.currentUser?.id);

    this.notificationService.getUnreadNotificationCount().subscribe((unreadCount) => {
      this.notificationService.unreadCount$.next(unreadCount);
    });

    this.globalService
      .select((state) => state.tenantInfo)
      .pipe(filter((tenantInfo) => !isNil(tenantInfo)))
      .subscribe({
        next: (tenantInfo) => {
          this.listShowNotification = [];
          for (const [moduleName, module] of Object.entries(tenantInfo?.ratePlan?.modulesAccessControl || {})) {
            if (module.enabled && this.notificationKindInfo[moduleName]) {
              this.listShowNotification.push(...this.notificationKindInfo[moduleName]);
            }
          }
          this.cdRef.markForCheck();
        }
      });
  }

  public toSocketNotification(notification: any) {
    return notification as NotificationFindInterface;
  }

  /** Глобальный шорткат ⌘K / Ctrl+K из нижней панели (`frm-informer-bottom-bar`). */
  public focusGlobalSearch(): void {
    this.globalSearch?.focusQueryField();
  }

  public onNotificationClick(toast: Toast, notification: NotificationFindInterface) {
    toast.messages = toast.messages.filter((message) => message.data.id !== notification.id);
    this.cdRef.detectChanges();

    this.notificationService.markOneAsRead(notification.id).subscribe(() => {
      this.websocketNotificationService.socketNotificationRead$.next(notification);
    });

    const commands = notification.action === NotificationActionEnum.PERSONAL_EXPORT_TASK_COMPLETE
      ? ['/user/settings']
      : [this.convertNotificationToLinkPipe.transform(notification)];
    this.router
      .navigate(commands)
      .catch(console.error);
  }
}
